import React, { useEffect, useState } from "react"
import { Button, Card, Collapse, Table } from "react-bootstrap"
import InfiniteScroll from "react-infinite-scroll-component"
import { toast } from "react-toastify"

import recordNotFound from "../../../assets/images/record_not_found.png"
import PageTitleButton from "../../../components/PageTitleButton"
import SearchBox from "../../../components/SearchBox"
import SweetAlerts from "../../../components/SweetAlerts"
import TooltipComponent from "../../../components/Tooltip"
import { admin } from "../../../config/apiEndpoints"
import axiosProvider from "../../../lib/AxiosProvider"
import {
  HasModulePermission,
  useHandleUnauthorizedAccess,
} from "../../../lib/GlobalMethods"
import { modulePermission } from "../../../lib/GlobalVariables"
import useDebounce from "../../../lib/hooks/useDebounce"

import AddEditFleetVehicle from "./AddEditFleetVehicle"
import FleetVehicleManagementSkeleton from "../../../skeleton/fleetManagement/fleetVehicleManagement"

const FleetVehicleManagement = () => {
  const canAdd = HasModulePermission(modulePermission.fleetManagement, "add")
  const canEdit = HasModulePermission(modulePermission.fleetManagement, "edit")
  const canDelete = HasModulePermission(modulePermission.fleetManagement, "delete")

  const [show, setShow] = useState(false)
  const [data, setData] = useState([])
  const [isUpdate, setIsUpdate] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [loading, setLoading] = useState(true)
  const [archived, setArchived] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [recordsCount, setRecordsCount] = useState({
    total_records: 0,
    active_records: 0,
    archived_records: 0,
  })

  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: false,
  })

  const [searchQuery, setSearchQuery] = useState("")
  const [hasMore, setHasMore] = useState(true)
  const [open, setOpen] = useState(false)
  const [filterObj, setFilterObj] = useState({
    active: false,
    archived: false,
  })

  const debouncedSearchQuery = useDebounce(searchQuery, 500)

  const { handleAccess } = useHandleUnauthorizedAccess()

  const handleShow = () => {
    setIsUpdate(false)
    setShow(true)
  }

  const handleClose = () => {
    setShow(false)
    setIsUpdate(false)
    setShowAlert(false)
  }

  const fetchData = async (search = debouncedSearchQuery) => {
    try {
      setLoading(true)

      const response = await axiosProvider({
        method: "GET",
        endpoint: `${admin.fleetVehicleList}?page=${pagination.current_page}&limit=10&search_term=${encodeURIComponent(
          search || "",
        )}&archived=${filterObj.archived}&active=${filterObj.active}`,
      })

      handleAccess({
        code: response?.data?.code,
      })

      if (response?.data?.code === 200) {
        const fetchedData = response?.data?.data || []

        setPagination(response?.data?.pagination)
        setRecordsCount(response?.data?.record_counts || recordsCount)

        if (
          !fetchedData.length ||
          response?.data?.pagination?.current_page ===
            response?.data?.pagination?.total_pages
        ) {
          setHasMore(false)
        } else {
          setHasMore(true)
        }

        setData((prevData) =>
          pagination.current_page === 1
            ? fetchedData
            : [...prevData, ...fetchedData],
        )
      }
    } catch (error) {
      toast.error(error?.message || "An error occurred")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData(debouncedSearchQuery)
  }, [debouncedSearchQuery, pagination.current_page, filterObj, refreshKey])

  const resetAndSetFilter = (filter) => {
    setFilterObj(filter)
    setPagination({ current_page: 1, total_pages: false })
    setData([])
    setLoading(true)
  }

  const fetchMoreData = () => {
    if (pagination.current_page < pagination.total_pages) {
      setPagination((prevState) => ({
        ...prevState,
        current_page: prevState.current_page + 1,
      }))
    } else {
      setHasMore(false)
    }
  }

  const handleArchiveRestore = async (id) => {
    try {
      setLoading(true)

      const response = await axiosProvider({
        method: archived ? "PATCH" : "DELETE",
        endpoint: archived
          ? admin.restoreFleetVehicle + id
          : admin.deleteFleetVehicle + id,
      })

      if (response?.data?.code === 200) {
        toast.success(response.data.message)

        handleClose()

        setPagination({
          current_page: 1,
          total_pages: false,
        })

        setData([])
        setRefreshKey((previousKey) => previousKey + 1)
      } else {
        throw new Error(response?.data?.message)
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong")
      setLoading(false)
    }
  }

  return (
    <>
      {canAdd ? (
        <PageTitleButton
          PagetitleText="Fleet Vehicle Management"
          HeaderButtonText="Add Fleet Vehicle"
          IconclassName="plus_black"
          onClick={handleShow}
          filterShow={true}
          filterClick={() => setOpen(!open)}
          open={open}
        />
      ) : (
        <PageTitleButton
          PagetitleText="Fleet Vehicle Management"
          filterShow={true}
          filterClick={() => setOpen(!open)}
          open={open}
        />
      )}

      <div className="details_cards_wrapper mb-3">
        <div
          className={`bg_sea_green details_card ${
            !filterObj.active && !filterObj.archived ? "card_active" : ""
          }`}
          onClick={() =>
            resetAndSetFilter({ active: false, archived: false })
          }
        >
          <h3 className="card_title">Total Fleet Vehicles</h3>
          <p className="tls_numbers">{recordsCount.total_records || 0}</p>
        </div>
        <div
          className={`bg_light_green details_card ${
            filterObj.active ? "card_active" : ""
          }`}
          onClick={() => resetAndSetFilter({ active: true, archived: false })}
        >
          <h3 className="card_title">Active Fleet Vehicles</h3>
          <p className="tls_numbers">{recordsCount.active_records || 0}</p>
        </div>
        <div
          className={`bg_light_red details_card ${
            filterObj.archived ? "card_active" : ""
          }`}
          onClick={() => resetAndSetFilter({ active: false, archived: true })}
        >
          <h3 className="card_title">Archived Fleet Vehicles</h3>
          <p className="tls_numbers">{recordsCount.archived_records || 0}</p>
        </div>
      </div>

      <Collapse in={open}>
        <div id="fleet-vehicle-filter-collapse" className="filters_sticky">
          <Card className="mb-3">
            <Card.Body>
              <SearchBox
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e?.target?.value)
                  setPagination({
                    current_page: 1,
                    total_pages: false,
                  })
                  setLoading(true)
                }}
              />
            </Card.Body>
          </Card>
        </div>
      </Collapse>

      {!loading ? (
        <Card>
          <Card.Body>
            <InfiniteScroll
              dataLength={data?.length || 0}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={<FleetVehicleManagementSkeleton infiniteScroll={true} rows={1} />}
            >
              <Table className="rounded mb-0 table-striped" responsive="md">
                <thead>
                  <tr>
                    <th>Vehicle ID</th>
                    <th>Registration Number</th>
                    <th>Fleet Vendor</th>
                    <th>Fleet Client</th>
                    {/* <th>Brand</th> */}
                    <th>Model</th>
                    <th>Vehicle Type</th>
                    <th>Status</th>
                    <th>Verification Status</th>
                    {(canEdit || canDelete) && (
                      <th className="text-center">Action</th>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {data?.length > 0 ? (
                    data.map((vehicle) => (
                      <tr key={vehicle.id}>
                        <td>{vehicle.id}</td>
                        <td>{vehicle.registration_number}</td>
                        <td>{vehicle?.fleet_vendor?.name || "Unassigned"}</td>
                        <td>
                          {vehicle?.fleet_clients && vehicle.fleet_clients.length > 0 ? (
                            <div className="d-flex flex-wrap gap-2">
                              {vehicle.fleet_clients.map((client) => (
                                <span key={client.id} className="badge bg-secondary">
                                  {client.name}
                                </span>
                              ))}
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                        {/* <td>{vehicle?.brand?.brand_name || "-"}</td> */}
                        <td>{vehicle?.model || "-"}</td>
                        <td>{vehicle?.vehicle_type || "-"}</td>
                        <td>
                          {vehicle?.deletedAt
                            ? "Archived"
                            : vehicle?.status || "Active"}
                        </td>
                        <td>
                          {vehicle?.verification_status
                            ?.replaceAll("_", " ") || "-"}
                        </td>

                        {(canEdit || canDelete) && (
                          <td className="text-center">
                            <div className="d-inline-flex gap-2">
                              {canEdit && !vehicle?.deletedAt && (
                                <TooltipComponent
                                  toolplace="top"
                                  tooltipText="Edit"
                                >
                                  <Button
                                    variant="btnedit_icon"
                                    className="btn_icon"
                                    onClick={() => {
                                      setIsUpdate(vehicle)
                                      setShow(true)
                                    }}
                                  >
                                    <i className="icon_main edit_pencil"></i>
                                  </Button>
                                </TooltipComponent>
                              )}

                              {canDelete && (
                                <TooltipComponent
                                  toolplace="top"
                                  tooltipText={
                                    vehicle?.deletedAt ? "Restore" : "Archive"
                                  }
                                >
                                  <Button
                                    variant={
                                      vehicle?.deletedAt
                                        ? "btnrestore_icon"
                                        : "btnarchive_icon"
                                    }
                                    className="btn_icon"
                                    onClick={() => {
                                      setIsUpdate(vehicle)
                                      setArchived(Boolean(vehicle?.deletedAt))
                                      setShowAlert(true)
                                    }}
                                  >
                                    <i
                                      className={`icon_main ${
                                        vehicle?.deletedAt
                                          ? "restore_data"
                                          : "archive_data"
                                      }`}
                                    ></i>
                                  </Button>
                                </TooltipComponent>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        className="text-center py-4"
                        colSpan={canEdit || canDelete ? 9 : 8}
                      >
                        <div className="norecord_wrapper">
                          <img src={recordNotFound} alt="No Record Found" />
                          <p className="norecords_title">No Records Found</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </InfiniteScroll>
          </Card.Body>
        </Card>
      ) : (
        <FleetVehicleManagementSkeleton />
      )}

      <AddEditFleetVehicle
        show={show}
        setShow={setShow}
        handleClose={handleClose}
        fetchData={() => {
          setPagination({
            current_page: 1,
            total_pages: false,
          })
          setData([])
          fetchData("")
        }}
        setIsUpdate={setIsUpdate}
        isUpdate={isUpdate}
      />

      <SweetAlerts
        showAlert={showAlert}
        loading={loading}
        alertMessage={`Are you sure you want to ${
          archived ? "Restore" : "Archive"
        } this Fleet Vehicle?`}
        handleClose={handleClose}
        iconClassName={
          archived ? "restore_data bg-black" : "archive_data bg-warning"
        }
        handleAlert={() => handleArchiveRestore(isUpdate?.id)}
      />
    </>
  )
}

export default FleetVehicleManagement
